import React from "react";
import type { SVGProps } from "react";
import { NavLink } from "react-router-dom";
import { tokens } from "../design-system/tokens";
import styles from "./NavLinkWithIcon.module.css";

type IconElement = React.ReactElement<SVGProps<SVGSVGElement>>;

interface NavLinkWithIconProps {
  to?: string;
  children: React.ReactNode;
  icon: IconElement;
  size?: number;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

function renderIcon(icon: IconElement) {
  const { className: iconClassName, style: iconStyle } = icon.props;

  return React.cloneElement(icon, {
    width: "100%",
    height: "100%",
    preserveAspectRatio: "xMidYMid meet",
    className: [styles.icon, iconClassName].filter(Boolean).join(" "),
    style: { color: tokens.colors.secondary, ...iconStyle },
    "aria-hidden": true,
  });
}

const NavLinkWithIcon: React.FC<NavLinkWithIconProps> = ({
  to,
  children,
  icon,
  size = 13,
  onClick,
  className,
  style,
}) => {
  const iconSlotStyle = {
    width: size,
    height: size,
  } as const;

  const content = (
    <span className={styles.content}>
      <span className={styles.iconSlot} style={iconSlotStyle}>
        {renderIcon(icon)}
      </span>
      <span className={styles.label}>{children}</span>
    </span>
  );

  const mergedClassName = [styles.link, className].filter(Boolean).join(" ");

  if (to) {
    return (
      <NavLink to={to} onClick={onClick} className={mergedClassName} style={style}>
        {content}
      </NavLink>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={mergedClassName}
      style={style}
    >
      {content}
    </div>
  );
};

export default NavLinkWithIcon;
